import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { SocialImportPlaceReview } from '@/components/SocialImport/SocialImportPlaceReview';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_TRIP_IMPORT_DETAILED_TOGGLED,
  EVENT_TRIP_IMPORT_REVIEW_FILTER_CHANGED,
} from '@/lib/analyticsEvents';
import type {
  ItineraryImportDraft,
  ItineraryImportSourceType,
  TripImportEntry,
  TripImportResultType,
} from '@/models/itineraryImportTypes';
import {
  countReviewFilterPlaces,
  type TripImportReviewFilter,
} from '@/utils/tripImportReviewFilters';
import type { SocialImportReviewSection } from '@/components/SocialImport/SocialImportPlaceReview';

export interface TripImportReviewStepProps {
  entry: TripImportEntry;
  importId: string;
  draft: ItineraryImportDraft;
  redactionLine: string | null;
  resultType: TripImportResultType;
  suggestedType: TripImportResultType;
  suggestionReason: string | null;
  reviewFilter: TripImportReviewFilter;
  onReviewFilterChange: (filter: TripImportReviewFilter) => void;
  reviewDetailed: boolean;
  onReviewDetailedChange: (enabled: boolean) => void;
  importMetaLine: string;
  importTitle: string;
  onImportTitleChange: (value: string) => void;
  totalPlaceCount: number;
  reviewSections: SocialImportReviewSection[];
  selectedIds: Set<string>;
  onTogglePlace: (clientId: string) => void;
  isBusy: boolean;
  onClose: () => void;
  onReRunImport: () => void;
  onCreateTrip: () => void;
  onCreateList: () => void;
  sourceType?: ItineraryImportSourceType;
  /** Social import only — saves selected places to My Travel (no trip/list notes). */
  onSaveToMyTravel?: () => void;
  onFindPlace?: (clientId: string) => void;
  onPlaceNotesChange?: (clientId: string, notes: string) => void;
  onPlaceDetailsExpandedChange?: (clientId: string, expanded: boolean) => void;
  /** Social import — Figma subcopy under title. */
  reviewIntroLine?: string | null;
  footerVariant?: 'default' | 'social';
  /** Social import — open trip/list picker to append places. */
  onAddToExisting?: () => void;
}

export function TripImportReviewStep({
  entry,
  importId,
  draft,
  redactionLine,
  suggestedType,
  suggestionReason,
  reviewFilter,
  onReviewFilterChange,
  reviewDetailed,
  onReviewDetailedChange,
  importMetaLine,
  importTitle,
  onImportTitleChange,
  totalPlaceCount,
  reviewSections,
  selectedIds,
  onTogglePlace,
  isBusy,
  onClose,
  onReRunImport,
  onCreateTrip,
  onCreateList,
  sourceType: _sourceType,
  onSaveToMyTravel,
  onFindPlace,
  onPlaceNotesChange,
  onPlaceDetailsExpandedChange,
  reviewIntroLine = null,
  footerVariant = 'default',
  onAddToExisting,
}: TripImportReviewStepProps) {
  const filterCounts = countReviewFilterPlaces(draft.days.flatMap((day) => day.places));
  const filterLabels: Record<TripImportReviewFilter, string> = {
    all: `All ${filterCounts.all}`,
    bookable: `Bookable ${filterCounts.bookable}`,
    custom: `Custom ${filterCounts.custom}`,
  };

  const suggestedLine = suggestionReason
    ? `Suggested: ${suggestionReason}`
    : suggestedType === 'itinerary'
      ? 'Suggested: Trip'
      : 'Suggested: List';

  const summaryText = draft.summary?.trim() ?? '';
  const selectedCount = selectedIds.size;
  const suggestTrip = suggestedType === 'itinerary';
  const saveDisabled = selectedCount === 0 || isBusy;
  const isSocialFooter = footerVariant === 'social';
  const subtitleLine = reviewIntroLine?.trim() || importMetaLine;

  return (
    <div className="trip-import-review-step flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="flex shrink-0 items-start justify-between gap-4 px-5 pb-4 pt-7 min-[768px]:px-8 min-[768px]:pr-7">
        <div className="min-w-0 flex-1">
          <label className="sr-only" htmlFor="trip-import-review-title">
            Trip name
          </label>
          <input
            id="trip-import-review-title"
            type="text"
            value={importTitle}
            onChange={(event) => onImportTitleChange(event.target.value)}
            disabled={isBusy}
            maxLength={120}
            className="w-full border-0 bg-transparent p-0 font-lora text-[26px] font-semibold leading-normal tracking-[0.3px] text-[#252528] outline-none placeholder:text-[#a7a7ae] disabled:opacity-50"
            placeholder={suggestTrip ? 'Name your trip' : 'Name your list'}
          />
          <p className="mt-1.5 font-manrope text-sm leading-5 tracking-[0.28px] text-[#525257]">
            {subtitleLine}
          </p>
        </div>
        <button
          type="button"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#f3f3f3] text-[#252528] hover:bg-[#e8e8e8] disabled:opacity-40"
          aria-label="Close import review"
          disabled={isBusy}
          onClick={onClose}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </header>

      {!isSocialFooter ? (
        <p className="shrink-0 px-5 pb-3 font-manrope text-xs text-[#525257] min-[768px]:px-8">{suggestedLine}</p>
      ) : null}

      <div className="flex shrink-0 flex-wrap items-center gap-2 px-5 pb-3 min-[768px]:px-8">
        {(['all', 'bookable', 'custom'] as const).map((filter) => {
          const active = reviewFilter === filter;
          return (
            <button
              key={filter}
              type="button"
              className={`rounded-[20px] px-3.5 py-1.5 font-manrope text-[13px] ${
                active
                  ? 'bg-[#252528] font-semibold text-white'
                  : 'border border-[#e1e1e7] bg-white font-normal text-[#252528]'
              }`}
              onClick={() => {
                onReviewFilterChange(filter);
                WebAnalytics.track(EVENT_TRIP_IMPORT_REVIEW_FILTER_CHANGED, {
                  entry,
                  import_id: importId,
                  filter,
                });
              }}
            >
              {filterLabels[filter]}
            </button>
          );
        })}
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 font-manrope text-[13px] text-[#525257]"
          onClick={() => {
            const next = !reviewDetailed;
            onReviewDetailedChange(next);
            WebAnalytics.track(EVENT_TRIP_IMPORT_DETAILED_TOGGLED, {
              entry,
              import_id: importId,
              enabled: next,
            });
          }}
        >
          Detailed
          <KeyboardArrowDownIcon
            sx={{ fontSize: 16, color: '#252528', transform: reviewDetailed ? 'rotate(180deg)' : undefined }}
          />
        </button>
      </div>

      <div className="mx-5 shrink-0 border-t border-[#e1e1e7] min-[768px]:mx-8" role="separator" />

      <div className="gt-keyboard-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-5 pt-5 min-[768px]:px-8">
        {summaryText || redactionLine ? (
          <div className="mb-5 flex shrink-0 flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-lora text-base font-semibold tracking-[0.3px] text-[#252528]">
                {isSocialFooter ? 'Overview' : 'Trip Overview'}
              </h2>
              <span className="inline-flex h-8 items-center rounded-2xl bg-[#f3f4f6] px-2 font-manrope text-sm tracking-[0.5px] text-[#6b7280]">
                AI generated
              </span>
            </div>
            {summaryText ? (
              <p className="font-manrope text-[13px] leading-normal text-[#525257]">{summaryText}</p>
            ) : null}
            {redactionLine ? (
              <p className="font-manrope text-xs leading-5 tracking-[0.24px] text-[#525257]">{redactionLine}</p>
            ) : null}
          </div>
        ) : null}

        <SocialImportPlaceReview
          variant="importDocs"
          previewLabel=""
          title={importTitle}
          onTitleChange={onImportTitleChange}
          titleInputLabel="Trip name"
          totalPlaceCount={totalPlaceCount}
          sections={reviewSections}
          selectedIds={selectedIds}
          onTogglePlace={onTogglePlace}
          onSelectAllToggle={() => {}}
          isBusy={isBusy}
          className="min-h-0 flex-1"
          onFindPlace={onFindPlace}
          onPlaceNotesChange={onPlaceNotesChange}
          onPlaceDetailsExpandedChange={onPlaceDetailsExpandedChange}
          socialImportPlaceRows={isSocialFooter}
          sourceExcerptLabel={isSocialFooter ? 'From the caption' : 'Excerpt from document'}
        />
      </div>

      {draft.from_cache ? (
        <p className="shrink-0 px-5 pt-2 font-manrope text-xs text-[#525257] min-[768px]:px-8">
          Loaded from a recent import cache
          {draft.prompt_version ? ` (${draft.prompt_version})` : ''}. Re-run to pick up the latest extraction
          (e.g. schedule times on place cards).{' '}
          <button
            type="button"
            className="font-semibold text-[#ff5212] underline"
            disabled={isBusy}
            onClick={onReRunImport}
          >
            Re-run import
          </button>
        </p>
      ) : null}

      <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-transparent px-5 pb-7 pt-4 min-[768px]:px-8">
        <p className="font-manrope text-xs text-[#525257]">
          {selectedCount} of {totalPlaceCount} selected
        </p>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {!isSocialFooter ? (
              <button
                type="button"
                className="rounded-xl px-5 py-3 font-manrope text-[15px] font-medium text-[#525257] hover:text-[#252528] disabled:opacity-40"
                disabled={isBusy}
                onClick={onClose}
              >
                Cancel
              </button>
            ) : null}
            {isSocialFooter ? (
              <>
                {onAddToExisting ? (
                  <button
                    type="button"
                    className="rounded-[26px] border border-[#e1e1e7] bg-white px-6 py-3 font-manrope text-[15px] font-semibold text-[#252528] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={saveDisabled}
                    onClick={onAddToExisting}
                  >
                    Add to existing
                  </button>
                ) : null}
                <button
                  type="button"
                  className="rounded-[20px] bg-[#ff5212] px-6 py-3 font-manrope text-[15px] font-bold text-white shadow-[0_0_10px_rgba(0,0,0,0.15)] disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={saveDisabled}
                  onClick={suggestTrip ? onCreateTrip : onCreateList}
                >
                  {suggestTrip ? 'Create trip' : 'Create list'}
                </button>
              </>
            ) : suggestTrip ? (
              <button
                type="button"
                className="rounded-[20px] bg-[#ff5212] px-6 py-3 font-manrope text-[15px] font-bold text-white shadow-[0_0_10px_rgba(0,0,0,0.15)] disabled:cursor-not-allowed disabled:opacity-40"
                disabled={saveDisabled}
                onClick={onCreateTrip}
              >
                Create trip
              </button>
            ) : (
              <button
                type="button"
                className="rounded-[20px] bg-[#ff5212] px-6 py-3 font-manrope text-[15px] font-bold text-white shadow-[0_0_10px_rgba(0,0,0,0.15)] disabled:cursor-not-allowed disabled:opacity-40"
                disabled={saveDisabled}
                onClick={onCreateList}
              >
                Create list
              </button>
            )}
          </div>
          {!isSocialFooter && suggestTrip ? (
            <button
              type="button"
              className="font-manrope text-xs font-medium text-[#525257] underline decoration-[#a7a7ae] underline-offset-2 hover:text-[#252528] disabled:cursor-not-allowed disabled:opacity-40"
              disabled={saveDisabled}
              onClick={onCreateList}
            >
              Save as list instead
            </button>
          ) : null}
          {!isSocialFooter && !suggestTrip ? (
            <button
              type="button"
              className="font-manrope text-xs font-medium text-[#525257] underline decoration-[#a7a7ae] underline-offset-2 hover:text-[#252528] disabled:cursor-not-allowed disabled:opacity-40"
              disabled={saveDisabled}
              onClick={onCreateTrip}
            >
              Save as trip instead
            </button>
          ) : null}
          {onSaveToMyTravel ? (
            <button
              type="button"
              className="font-manrope text-xs font-medium text-[#525257] underline decoration-[#a7a7ae] underline-offset-2 hover:text-[#252528] disabled:cursor-not-allowed disabled:opacity-40"
              disabled={saveDisabled}
              onClick={onSaveToMyTravel}
            >
              {isSocialFooter ? 'Keep in Saved only' : 'Save spots to My Travel (places only)'}
            </button>
          ) : null}
        </div>
      </footer>
    </div>
  );
}
